import React, { useState, useEffect } from 'react';
import { useRole, BACKEND_URL } from '../App';
import { StatusBadge, ListSkeleton, EmptyState, PageHeader } from '../components/shared';
import { toast } from 'sonner';
import { AlertTriangle, Plus, X, Calendar, Tag, Shield, FileText, Clock } from 'lucide-react';

export default function IncidentRegister() {
  const { role, canWrite } = useRole();
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    title: '',
    date: new Date().toISOString().split('T')[0],
    category: '',
    risk_level: '',
    description: '',
    immediate_action: '',
  });

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/incidents`)
      .then(r => r.json())
      .then(d => { setIncidents(d.incidents || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setSubmitting(true);
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/incidents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, role, status: 'Open' })
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      
      // Optimistic add
      setIncidents(prev => [{
        id: data.incident_id,
        incident_id: form.title,
        date: form.date,
        category: form.category,
        risk_level: form.risk_level,
        status: 'Open',
        description: form.description,
        immediate_action: form.immediate_action,
        created_time: new Date().toISOString(),
      }, ...prev]);
      
      toast.success('Incident created successfully');
      setShowCreate(false);
      setForm({ title: '', date: new Date().toISOString().split('T')[0], category: '', risk_level: '', description: '', immediate_action: '' });
    } catch {
      toast.error('Failed to create incident');
    }
    setSubmitting(false);
  };

  const CATEGORIES = ['Operational', 'Safety', 'Client', 'Equipment', 'Compliance'];
  const RISK_LEVELS = ['Low', 'Medium', 'High', 'Critical'];

  const getRiskColor = (level) => {
    switch (level?.toLowerCase()) {
      case 'critical': return 'badge-critical';
      case 'high': return 'badge-critical';
      case 'medium': return 'badge-draft';
      case 'low': return 'badge-active';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <div className="page-container" data-testid="incident-register">
      <PageHeader title="Incident Register" subtitle="Operational incident log and tracking" />

      {loading ? <ListSkeleton /> : incidents.length > 0 ? (
        <div className="space-y-3" data-testid="incident-log-list">
          {incidents.map(inc => (
            <div key={inc.id} className="ps-card-static p-5" data-testid={`incident-item-${inc.id}`}>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'hsl(0 80% 94%)' }}>
                  <AlertTriangle size={20} style={{ color: 'hsl(0, 72%, 34%)' }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="text-sm font-semibold">{inc.incident_id || 'Untitled Incident'}</h3>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {inc.risk_level && (
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${getRiskColor(inc.risk_level)}`}>
                          <Shield size={10} />{inc.risk_level}
                        </span>
                      )}
                      {inc.status && <StatusBadge status={inc.status} />}
                    </div>
                  </div>
                  {inc.description && (
                    <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{inc.description}</p>
                  )}
                  <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                    {inc.date && (
                      <span className="flex items-center gap-1">
                        <Calendar size={12} />
                        {new Date(inc.date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </span>
                    )}
                    {inc.category && (
                      <span className="flex items-center gap-1"><Tag size={12} />{inc.category}</span>
                    )}
                    {inc.responsible_role && (
                      <span>{inc.responsible_role}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <EmptyState
          icon={AlertTriangle}
          title="No incidents recorded"
          description={canWrite ? 'Click the + button to log your first incident.' : 'No incidents have been recorded yet.'}
        />
      )}

      {/* FAB Create Button */}
      {canWrite && (
        <button
          className="fab"
          onClick={() => setShowCreate(true)}
          data-testid="incident-fab-create-button"
        >
          <Plus size={24} />
        </button>
      )}

      {/* Create Incident Modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
          <div className="fixed inset-0 bg-black/40" onClick={() => setShowCreate(false)} />
          <div
            className="relative w-full sm:max-w-lg bg-card rounded-t-2xl sm:rounded-2xl shadow-[var(--shadow-panel)] p-6 z-10 max-h-[90vh] overflow-y-auto"
            data-testid="incident-create-drawer"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold">New Incident</h2>
              <button onClick={() => setShowCreate(false)} className="p-2 rounded-lg hover:bg-muted">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Title *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  className="w-full px-3 py-2.5 rounded-xl border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="Brief incident title"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">Date</label>
                  <input
                    type="date"
                    value={form.date}
                    onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                    className="w-full px-3 py-2.5 rounded-xl border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Category</label>
                  <select
                    value={form.category}
                    onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                    className="w-full px-3 py-2.5 rounded-xl border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value="">Select...</option>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Risk Level</label>
                <div className="flex gap-2">
                  {RISK_LEVELS.map(level => (
                    <button
                      key={level}
                      type="button"
                      onClick={() => setForm(f => ({ ...f, risk_level: f.risk_level === level ? '' : level }))}
                      className={`filter-chip ${form.risk_level === level ? 'active' : ''}`}
                    >
                      {level}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Description</label>
                <textarea
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  className="w-full px-3 py-2.5 rounded-xl border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring min-h-[80px] resize-none"
                  placeholder="Describe what happened..."
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Immediate Action Taken</label>
                <textarea
                  value={form.immediate_action}
                  onChange={e => setForm(f => ({ ...f, immediate_action: e.target.value }))}
                  className="w-full px-3 py-2.5 rounded-xl border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring min-h-[60px] resize-none"
                  placeholder="What was done immediately..."
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="w-full py-3 rounded-xl text-sm font-semibold transition-colors disabled:opacity-50"
                style={{ background: 'hsl(var(--primary))', color: 'hsl(var(--primary-foreground))' }}
                data-testid="incident-create-submit-button"
              >
                {submitting ? 'Creating...' : 'Create Incident'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
